import { defineStore } from "pinia"
import { ref, computed } from "vue"
import type { User, LoginResponse } from "@/types"
import { authService } from "@/services/auth"
import { apiService } from "@/services/api"
import { i18n } from "@/i18n"
// import { useTawk } from "@/composables/useTawk"

export const useAuthStore = defineStore("auth", () => {
  const user = ref<User | null>(null)
  const isLoading = ref(false)
  const isInitializing = ref(false) // Flag to prevent duplicate initialization

  const isAuthenticated = computed(() => !!user.value)
  const isRestaurantOwner = computed(() => user.value?.role === "restaurant_owner")
  const isAdmin = computed(() => user.value?.role === "admin")

  const login = async (email: string, password: string, turnstileToken?: string): Promise<LoginResponse> => {
    console.log("🔐 [AuthStore] Login attempt", { email })
    isLoading.value = true
    try {
      const response = await authService.login(email, password, turnstileToken)

      user.value = response.user

      console.log("✅ [AuthStore] Login successful", {
        userId: response.user.id,
        role: response.user.role,
        email: response.user.email,
      })

      // Set language preference from user profile
      if (response.user?.currentLanguage && (response.user.currentLanguage === "pl" || response.user.currentLanguage === "en")) {
        i18n.global.locale.value = response.user.currentLanguage
        localStorage.setItem("app_language", response.user.currentLanguage)
      }

      // Reset refresh failed flag and start proactive refresh after successful login
      apiService.resetRefreshFailed()

      // Set user attributes in Tawk.to (Tawk.io implementation commented out)
      // const { setUserAttributes } = useTawk()
      // await setUserAttributes(response.user)

      return response
    } catch (error) {
      console.error("❌ [AuthStore] Login failed:", error)
      throw error
    } finally {
      isLoading.value = false
    }
  }

  const logout = async () => {
    console.log("🚪 [AuthStore] Logout initiated", {
      userId: user.value?.id,
      email: user.value?.email,
    })
    try {
      // Stop proactive token refresh
      apiService.stopProactiveRefresh()
      // Call backend logout to clear cookies
      await authService.logout()
      console.log("✅ [AuthStore] Logout successful")
    } catch (error) {
      console.error("❌ [AuthStore] Error during logout:", error)
    } finally {
      // Clear user attributes in Tawk.to (Tawk.io implementation commented out)
      // const { clearUserAttributes } = useTawk()
      // await clearUserAttributes()

      user.value = null
      console.log("🧹 [AuthStore] User data cleared")
    }
  }

  const initializeAuth = async () => {
    console.log("🔄 [AuthStore] initializeAuth called", {
      isInitializing: isInitializing.value,
      hasRefreshFailed: apiService.hasRefreshFailed(),
    })

    // Prevent duplicate initialization
    if (isInitializing.value) {
      console.log("⏳ [AuthStore] Auth initialization already in progress, skipping")
      return
    }

    // CRITICAL: If refresh has already failed, don't try to initialize
    // This prevents infinite loops when there are no tokens
    if (apiService.hasRefreshFailed()) {
      console.warn(
        "⚠️ [AuthStore] Refresh already failed, skipping auth initialization to prevent infinite loop"
      )
      user.value = null
      isInitializing.value = false
      return
    }

    isInitializing.value = true
    console.log("🔍 [AuthStore] Fetching user profile...")

    try {
      // Try to get user profile (uses cookie automatically)
      const userData = await authService.getProfile()
      user.value = userData
      console.log("✅ [AuthStore] User profile fetched successfully", {
        userId: userData?.id,
        role: userData?.role,
        email: userData?.email,
        isAnonymous: userData?.isAnonymous,
      })

      // Set language preference from user profile
      if (userData?.currentLanguage && (userData.currentLanguage === "pl" || userData.currentLanguage === "en")) {
        i18n.global.locale.value = userData.currentLanguage
        localStorage.setItem("app_language", userData.currentLanguage)
        console.log("🌐 [AuthStore] Language set to:", userData.currentLanguage)
      }

      // Start proactive token refresh if user is authenticated
      if (userData) {
        console.log("🔄 [AuthStore] Starting proactive token refresh")
        apiService.startProactiveRefresh()

        // Set user attributes in Tawk.to (Tawk.io implementation commented out)
        // const { setUserAttributes } = useTawk()
        // await setUserAttributes(userData)
      }
    } catch (error: unknown) {
      const err = error as { response?: { status?: number }; message?: string }
      console.error("❌ [AuthStore] Error fetching user profile:", {
        status: err?.response?.status,
        message: err?.message,
      })

      // CRITICAL: If error is 401 and refresh has failed, don't try again
      // This prevents infinite loops when tokens are missing
      if (err?.response?.status === 401 && apiService.hasRefreshFailed()) {
        console.warn(
          "⚠️ [AuthStore] 401 error and refresh already failed - preventing infinite loop"
        )
        user.value = null
        isInitializing.value = false
        // Don't throw error - just return silently to prevent further retries
        return
      }

      // If error is 401 but refresh hasn't failed yet, wait for refresh to complete
      if (err?.response?.status === 401 && !apiService.hasRefreshFailed()) {
        console.log("⏳ [AuthStore] 401 error, waiting for refresh to complete...")
        try {
          // Wait for refresh to complete (max 2 seconds)
          await apiService.waitForTokenRefresh()
          // If refresh succeeded, try getProfile again
          if (!apiService.hasRefreshFailed()) {
            console.log("✅ [AuthStore] Refresh succeeded, retrying getProfile...")
            const userData = await authService.getProfile()
            user.value = userData
            // Set language preference from user profile
            if (userData?.currentLanguage && (userData.currentLanguage === "pl" || userData.currentLanguage === "en")) {
              i18n.global.locale.value = userData.currentLanguage
              localStorage.setItem("app_language", userData.currentLanguage)
              console.log("🌐 [AuthStore] Language set to:", userData.currentLanguage)
            }
            if (userData) {
              apiService.startProactiveRefresh()

              // Set user attributes in Tawk.to (Tawk.io implementation commented out)
              // const { setUserAttributes } = useTawk()
              // await setUserAttributes(userData)
            }
            return
          }
        } catch (waitError) {
          console.error("❌ [AuthStore] Error waiting for refresh:", waitError)
        }
      }

      // User not authenticated or token expired
      user.value = null
      console.log("🧹 [AuthStore] User data cleared due to error")
    } finally {
      isInitializing.value = false
      console.log("🏁 [AuthStore] Auth initialization finished", {
        isAuthenticated: !!user.value,
        hasRefreshFailed: apiService.hasRefreshFailed(),
      })
    }
  }

  return {
    user,
    isLoading,
    isInitializing,
    isAuthenticated,
    isRestaurantOwner,
    isAdmin,
    login,
    logout,
    initializeAuth,
  }
})
